import {
  createMutationKeys,
  createQueryKeys,
  mergeQueryKeys,
} from '@lukemorales/query-key-factory'
import { APIAxiosInstance } from '@core/utils/api-axios-instance'
import { ITask } from '@core/entities/task/Task'
import {
  uploadProofs,
  uploadTemplateAnswerFiles,
} from '@core/react-query/features/permits/permit/helpers'
import { createUploadProgressReporter } from '@core/react-query/helpers'
import { RequestTypes } from './requestTypes'
import { createQueryKeyString } from '@core/react-query/queryParams'

const taskQueryKeys = createQueryKeys('task', {
  byId: ({ taskId }: RequestTypes['getTask']) => {
    return {
      queryKey: [taskId],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(`tasks/${taskId}`)

        return data as ITask
      },
      contextQueries: {
        linkedProcesses: {
          queryKey: null,
          queryFn: async () => {
            const { data } = await APIAxiosInstance.get(
              `tasks/${taskId}/linked-processes`,
            )

            return data
          },
        },
      },
    }
  },
  timeline: ({ taskId, page, size }: RequestTypes['getTaskTimeline']) => {
    return {
      queryKey: [taskId, page, size],
      queryFn: async ({ pageParam = 1 }) => {
        const queryString = createQueryKeyString({
          taskId,
          page: page ? page : pageParam,
          size,
        })

        const { data } = await APIAxiosInstance.get(
          `audits/tasks/${taskId}${queryString}`,
        )

        return data
      },
    }
  },
  draftById: ({ draftId }: RequestTypes['getTaskDraft']) => {
    return {
      queryKey: [draftId],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(`tasks/drafts/${draftId}`)
        return data
      },
    }
  },
  assignableUsers: ({
    projectId,
    templateId,
    stepId,
    term,
    page,
    size,
  }: RequestTypes['getTaskAssignableUsers']) => {
    return {
      queryKey: [projectId, templateId, stepId, term, page, size],
      queryFn: async ({ pageParam = 1 }) => {
        const queryParams = createQueryKeyString({
          projectId,
          templateId,
          stepId,
          term,
          page: page ? page : pageParam,
          size,
        })

        const { data } = await APIAxiosInstance.get(
          `tasks/assignable-users${queryParams}`,
        )

        return data
      },
    }
  },
})

const taskMutationKeys = createMutationKeys('task', {
  createTask: {
    mutationKey: null,
    mutationFn: async ({
      projectId,
      stepAnswers,
      attachments,
      offlineSubmittedOn,
      idempotencyKey,
      onSubmissionProgress,
      ...mutation
    }: RequestTypes['createTask']) => {
      const createFileUploadProgressReporter = createUploadProgressReporter(
        stepAnswers.answers,
        onSubmissionProgress,
        attachments?.data,
      )
      let uploadedAttachments
      if (attachments) {
        uploadedAttachments = await uploadProofs(
          attachments.data,
          `${mutation.taskTemplateId}-task-attachments`,
          createFileUploadProgressReporter,
        )
      }

      const uploadedAnswers = await uploadTemplateAnswerFiles({
        answers: stepAnswers.answers,
        templateId: mutation.taskTemplateId,
        createFileUploadProgressReporter,
      })

      const { data } = await APIAxiosInstance.post(
        `tasks?projectId=${projectId}`,
        {
          ...mutation,
          stepAnswers: { answers: uploadedAnswers },
          attachments: { data: uploadedAttachments },
        },
        {
          headers: {
            'X-OFFLINE-SUBMITTED-ON': offlineSubmittedOn,
            'X-IDEMPOTENCY-KEY': idempotencyKey,
          },
        },
      )
      onSubmissionProgress?.(1)
      return data as ITask
    },
  },
  createTaskDraft: {
    mutationKey: null,
    mutationFn: async ({
      stepAnswers,
      offlineSubmittedOn,
      idempotencyKey,
      attachments,
      onSubmissionProgress,
      ...mutation
    }: RequestTypes['createTaskDraft']) => {
      const createFileUploadProgressReporter = createUploadProgressReporter(
        stepAnswers?.answers,
        onSubmissionProgress,
        attachments?.data,
      )
      let uploadedAttachments

      if (attachments) {
        uploadedAttachments = await uploadProofs(
          attachments.data,
          `${mutation.taskTemplateId}-task-attachments`,
          createFileUploadProgressReporter,
        )
      }

      const uploadedAnswers = await uploadTemplateAnswerFiles({
        answers: stepAnswers.answers,
        templateId: mutation.taskTemplateId,
        createFileUploadProgressReporter,
      })

      const { data } = await APIAxiosInstance.post(
        `tasks/drafts?projectId=${mutation.projectId}`,
        {
          ...mutation,
          stepAnswers: { answers: uploadedAnswers },
          attachments: { data: uploadedAttachments },
        },
        {
          headers: {
            'X-OFFLINE-SUBMITTED-ON': offlineSubmittedOn,
            'X-IDEMPOTENCY-KEY': idempotencyKey,
          },
        },
      )

      onSubmissionProgress?.(1)
      return data as ITask
    },
  },
  updateTaskDraft: {
    mutationKey: null,
    mutationFn: async ({
      id,
      stepAnswers,
      offlineSubmittedOn,
      idempotencyKey,
      attachments,
      onSubmissionProgress,
      ...mutation
    }: RequestTypes['updateTaskDraft']) => {
      const createFileUploadProgressReporter = createUploadProgressReporter(
        stepAnswers?.answers,
        onSubmissionProgress,
        attachments?.data,
      )
      let uploadedAttachments
      if (attachments) {
        uploadedAttachments = await uploadProofs(
          attachments.data,
          `${mutation.taskTemplateId}-task-attachments`,
          createFileUploadProgressReporter,
        )
      }

      const uploadedAnswers = await uploadTemplateAnswerFiles({
        answers: stepAnswers.answers,
        templateId: mutation.taskTemplateId,
        createFileUploadProgressReporter,
      })

      const { data } = await APIAxiosInstance.put(
        `tasks/drafts/${id}`,
        {
          ...mutation,
          stepAnswers: { answers: uploadedAnswers },
          attachments: { data: uploadedAttachments },
        },
        {
          headers: {
            'X-OFFLINE-SUBMITTED-ON': offlineSubmittedOn,
            'X-IDEMPOTENCY-KEY': idempotencyKey,
          },
        },
      )

      onSubmissionProgress?.(1)
      return data as ITask
    },
  },
  deleteTaskDraft: {
    mutationKey: null,
    mutationFn: async ({ draftId }: RequestTypes['deleteTaskDraft']) => {
      const { data } = await APIAxiosInstance.delete(`tasks/drafts/${draftId}`)
      return data
    },
  },
  editTask: {
    mutationKey: null,
    mutationFn: async ({ id, ...payload }: RequestTypes['editTask']) => {
      const { data } = await APIAxiosInstance.put(`tasks/${id}`, payload)

      return data
    },
  },

  cancelTask: {
    mutationKey: null,
    mutationFn: async ({ id, notes }: RequestTypes['cancelTask']) => {
      const { data } = await APIAxiosInstance.post(`tasks/${id}/cancel`, {
        notes,
      })

      return data
    },
  },

  reopenTask: {
    mutationKey: null,
    mutationFn: async ({
      id,
      currentStepId,
      assigneeUserId,
      reason,
    }: RequestTypes['reopenTask']) => {
      const { data } = await APIAxiosInstance.post(`tasks/${id}/reopen`, {
        currentStepId,
        assigneeUserId,
        reason,
      })

      return data
    },
  },

  reassignTask: {
    mutationKey: null,
    mutationFn: async ({
      id,
      assigneeUserId,
      notes,
    }: RequestTypes['reassignTask']) => {
      const { data } = await APIAxiosInstance.post(`tasks/${id}/reassign`, {
        assigneeUserId,
        notes,
      })

      return data
    },
  },

  requestChangesTask: {
    mutationKey: null,
    mutationFn: async ({
      id,
      assigneeUserId,
      comment,
    }: RequestTypes['requestChangesTask']) => {
      const { data } = await APIAxiosInstance.post(
        `tasks/${id}/request-changes`,
        {
          assigneeUserId,
          comment,
        },
      )

      return data
    },
  },

  submitTaskStep: {
    mutationKey: null,
    mutationFn: async ({
      id,
      taskTemplateId,
      assigneeUserId,
      stepAnswers,
    }: RequestTypes['submitTaskStep']) => {
      const uploadedAnswers = await uploadTemplateAnswerFiles({
        answers: stepAnswers.answers,
        templateId: taskTemplateId,
      })

      const { data } = await APIAxiosInstance.post(`tasks/${id}/submit-step`, {
        assigneeUserId,
        stepAnswers: { answers: uploadedAnswers },
      })

      return data
    },
  },

  pdfDownloadUrl: {
    mutationKey: null,
    mutationFn: async ({ taskId }: RequestTypes['getPdfDownloadUrl']) => {
      const { data } = await APIAxiosInstance.post(`tasks/${taskId}/download`)

      return data
    },
  },
})

const taskKeys = mergeQueryKeys(taskQueryKeys, taskMutationKeys).task

export { taskKeys }
